import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import AddressFormatterFactory from 'common/Addresses/AddressFormatters/AddressFormatterFactory';
import AddressTypes from 'common/Forms/AddressTypes';
import formTypes from 'common/Forms/FormTypes';
import ModalWrapper from 'components/Modals/ModalWrapper';

const InputId = {
  AddressLine1: 'addressLine1',
  AddressLine2: 'addressLine2',
  AddressLine3: 'addressLine3',
  AddressLine4: 'addressLine4',
  AddressPostalCode: 'addressPostalCode',
  AddressCountry: 'addressCountry'
};

function findJurisdictionByToken(jurisdictions, token) {
  const normalisedToken = token?.toLowerCase();
  if (!normalisedToken) {
    return null;
  }
  return jurisdictions.find((j) => {
    if (!j) {
      return false;
    }
    const candidateNames = [
      j.name,
      ...(j.aliases || [])
    ];

    return candidateNames
      .filter(x => typeof x === 'string')
      .map(x => x.toLowerCase())
      .includes(normalisedToken);
  }) || null;
}

function applyW9CountryInference(initialAddress, jurisdictions, formType) {
  if (!formType?.family || formType.family.toLowerCase() !== 'w9') {
    return initialAddress;
  }

  if (!jurisdictions || jurisdictions.length === 0) {
    return initialAddress;
  }

  const normalisedAddress = {
    ...initialAddress
  };

  const lastAddressLine = (normalisedAddress.addressLine4 && normalisedAddress.addressLine4.trim())
    || (normalisedAddress.addressLine3 && normalisedAddress.addressLine3.trim())
    || '';

  if (!lastAddressLine) {
    return normalisedAddress;
  }

  const lastToken = lastAddressLine
    .split(',')
    .map(x => x.trim())
    .filter(x => x)
    .pop();

  if (!lastToken) {
    return normalisedAddress;
  }

  const matchedJurisdiction = findJurisdictionByToken(jurisdictions, lastToken);

  let updatedAddress = { ...normalisedAddress };

  const lastAddressLineParts = lastAddressLine
    .split(',')
    .map(x => x.trim())
    .filter(x => x);

  if (lastAddressLineParts.length > 0) {
    const line3 = lastAddressLineParts.length >= 1 ? lastAddressLineParts[0] : '';
    let line4 = lastAddressLineParts.length === 2 && (!matchedJurisdiction || (matchedJurisdiction && matchedJurisdiction.name === 'United States of America')) ? lastAddressLineParts[1] : '';
    if (line4 === '') {
      line4 = lastAddressLineParts.length > 2 ? lastAddressLineParts[1] : '';
    }
    let postalCode = lastAddressLineParts.length === 3 && (!matchedJurisdiction || (matchedJurisdiction && matchedJurisdiction.name === 'United States of America')) ? lastAddressLineParts[2] : '';
    if (postalCode === '') {
      postalCode = lastAddressLineParts.length > 3 ? lastAddressLineParts[2] : '';
    }

    updatedAddress = {
      ...updatedAddress,
      addressLine3: line3,
      addressLine4: line4,
      addressPostalCode: postalCode,
      addressCountry: matchedJurisdiction ? matchedJurisdiction.name : 'United States of America'
    };
  }

  return {
    ...updatedAddress
  };
}

const EditAddressModal = ({
  dispatch,
  formName,
  formType,
  addressProps,
  isEditAddressModalShown,
  formAddressValues,
  onCancel,
  jurisdictions
}) => {
  const addressFormatter = AddressFormatterFactory(formType);

  const formAddress = addressFormatter?.GetWellFormedAddress(addressProps, formAddressValues);
  const initialWellFormedAddress = applyW9CountryInference({
    addressLine1: formAddress?.line1 ?? '',
    addressLine2: formAddress?.line2 ?? '',
    addressLine3: formAddress?.line3 ?? '',
    addressLine4: formAddress?.line4 ?? '',
    addressPostalCode: formAddress?.postalCode ?? '',
    addressCountry: formAddress?.country ?? ''
  }, jurisdictions, formType);

  const [wellFormedAddress, setAddressValues] = useState(initialWellFormedAddress);

  const [errorMessages, setErrorMessages] = useState({
    addressLine2: '',
    addressLine4: '',
    addressPostalCode: ''
  });

  const dispatchAddress = (value) => {
    addressFormatter?.GetFieldPathValueMap(addressProps, value)?.map(x => dispatch(change(formName, x.key, x.value)));
  };

  const onConfirm = (value) => {
    dispatchAddress(value);
    onCancel();
  };

  const onChangeHandler = (e) => {
    const { name, value } = e.target;

    if ([InputId.AddressLine2, InputId.AddressLine4, InputId.AddressPostalCode].includes(name)) {
      if (value.includes(',')) {
        setErrorMessages({ ...errorMessages, [name]: 'Commas not supported. Please use other delineator.' });
      } else {
        setErrorMessages({ ...errorMessages, [name]: '' });
      }
    }
    setAddressValues({ ...wellFormedAddress, [name]: value });
  };

  const hasErrors = () => Object.values(errorMessages).some(errorMessage => errorMessage !== '');

  return (
    <ModalWrapper
      headerClose={ false }
      isOpen={ isEditAddressModalShown }
      btnConfirmTitle="Submit"
      btnCancelTitle="Cancel"
      title={ `Edit ${ AddressTypes.getDescription(addressProps?.addressType) }` }
      subTitle="These inputs reflect how the address is stored on a Customer/Account record."
      modalSize="edit-address-modal"
      disableConfirm={ hasErrors }
      onConfirm={ () => onConfirm(wellFormedAddress) }
      onCancel={ onCancel }
    >
      <form className="form edit-address-modal">
        <div className="columns col-12">
          <div className="col-6 form-group">
            <label>Address Line 1</label>
            <input
              id={ InputId.AddressLine1 }
              name={ InputId.AddressLine1 }
              className="form-input"
              value={ wellFormedAddress.addressLine1 }
              onChange={ onChangeHandler }
            />
          </div>
          <div className="col-6">
            <label>Address Line 2</label>
            <input
              id={ InputId.AddressLine2 }
              name={ InputId.AddressLine2 }
              className={ `form-input ${ errorMessages.addressLine2 ? 'is-error' : '' }` }
              value={ wellFormedAddress.addressLine2 }
              onChange={ onChangeHandler }
            />
            {errorMessages.addressLine2 && (
              <div className="error-message">{errorMessages.addressLine2}</div>
            )}
          </div>
        </div>
        <div className="columns col-12 mt-2">
          <div className="col-6 form-group">
            <label>Address Line 3</label>
            <input
              id={ InputId.AddressLine3 }
              name={ InputId.AddressLine3 }
              className="form-input"
              value={ wellFormedAddress.addressLine3 }
              onChange={ onChangeHandler }
            />
          </div>
          <div className="col-6">
            <label>Address Line 4</label>
            <input
              id={ InputId.AddressLine4 }
              name={ InputId.AddressLine4 }
              className={ `form-input ${ errorMessages.addressLine4 ? 'is-error' : '' }` }
              value={ wellFormedAddress.addressLine4 }
              onChange={ onChangeHandler }
            />
            {errorMessages.addressLine4 && (
              <div className="error-message">{errorMessages.addressLine4}</div>
            )}
          </div>
        </div>
        <div className="columns col-12 mt-2">
          <div className="col-4 form-group">
            <label>Postal Code</label>
            <input
              id={ InputId.AddressPostalCode }
              name={ InputId.AddressPostalCode }
              className={ `form-input ${ errorMessages.addressPostalCode ? 'is-error' : '' }` }
              value={ wellFormedAddress.addressPostalCode }
              onChange={ onChangeHandler }
            />
            {errorMessages.addressPostalCode && (
              <div className="error-message">{errorMessages.addressPostalCode}</div>
            )}
          </div>
          <div className="col-8">
            <label>Country</label>
            <select
              id={ InputId.AddressCountry }
              name={ InputId.AddressCountry }
              className="form-select"
              value={ wellFormedAddress.addressCountry }
              onChange={ onChangeHandler }
              disabled={ !AddressTypes.hasCountry(addressProps?.addressType) }
            >
              { !((jurisdictions?.map(item => item.name) || []).includes(wellFormedAddress.addressCountry))
                && wellFormedAddress.addressCountry !== ''
                && <option disabled value={ wellFormedAddress.addressCountry }>{ wellFormedAddress.addressCountry }</option> }
              <option value="">Please select...</option>
              { (jurisdictions?.map(item => <option key={ item.name } value={ item.name }>{ item.name }</option>) || []) }
            </select>
          </div>
        </div>
      </form>
    </ModalWrapper>
  );
};

EditAddressModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  formName: PropTypes.string.isRequired,
  formType: PropTypes.shape().isRequired,
  addressProps: PropTypes.shape({
    addressType: PropTypes.number,
    substantialOwnerIndex: PropTypes.number
  }),
  formAddressValues: PropTypes.shape(),
  isEditAddressModalShown: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  jurisdictions: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired
  }))
};

EditAddressModal.defaultProps = {
  isEditAddressModalShown: false,
  addressProps: null,
  formAddressValues: null,
  jurisdictions: null
};

const mapDispatchToProps = dispatch => ({ dispatch });

const mapStateToProps = (state) => {
  const {
    forms: {
      addressProps
    },
    common: {
      jurisdictions
    }
  } = state;

  const match = useParams(window.location.href);
  const formType = formTypes.getByCode(match?.blankType);

  const formName = formTypes.getFormName(formType?.family);
  const formAddressValues = state?.form && state.form[formName]?.values;

  return {
    formAddressValues,
    addressProps,
    formName,
    formType,
    jurisdictions
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EditAddressModal);
